// client/src/components/profissionais/ProfissionaisPage.js
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/auth/AuthProvider';
import { useProfissionaisQuery } from '../../hooks/useProfissionais'; 
import { useUnidadesPorConta } from '../../hooks/useUnidades'; 
import { PlusCircle, Search, Filter, Loader2, UserPlus, X, ChevronDown, Briefcase, Grid3x3, List, Eye, MapPin, Settings } from 'lucide-react';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import useAlert from '../../hooks/useAlert';
import PermissionGuard from '../routing/PermissionGuard';
import ProfissionalCard from './ProfissionalCard';
import ProfissionaisRow from './ProfissionaisRow';
import Pagination from '../common/Pagination';
import ActionsDrawer from '../common/ActionsDrawer';

const extractId = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    return String(obj._id || obj.id || obj);
};

const initialFilterState = {
    search: '',
    unidade: '',
    status: 'todos' 
};

const ProfissionaisPage = () => {
    const { user } = useAuth();
    const { setAlert } = useAlert();
    const navigate = useNavigate();
    
    const [currentPage, setCurrentPage] = useState(1);
    const [showFilters, setShowFilters] = useState(false);
    const [viewMode, setViewMode] = useState('table'); 
    
    const [localFilters, setLocalFilters] = useState(initialFilterState);
    const [activeFilters, setActiveFilters] = useState(initialFilterState);
    const [selectedProfissional, setSelectedProfissional] = useState(null);

    const profissionaisPerPage = 12;
    const contaId = extractId(user?.account);

    const queryParams = useMemo(() => {
        const params = {
            accountId: contaId,
            page: currentPage,
            limit: profissionaisPerPage,
            search: activeFilters.search,
            unidadeId: activeFilters.unidade
        };
        
        if (activeFilters.status !== 'todos') {
            params.status = activeFilters.status;
        }
        
        return params;
    }, [contaId, currentPage, activeFilters]);

    const { 
        data: profData, 
        isLoading: loadingProfissionais, 
        isError, 
        refetch: refetchProfissionais 
    } = useProfissionaisQuery(queryParams, { enabled: !!contaId });

    const { data: unidadesResponse } = useUnidadesPorConta(contaId, { enabled: !!contaId, limit: 1000 });
    const unidadesDaConta = useMemo(() => Array.isArray(unidadesResponse?.data) ? unidadesResponse.data : [], [unidadesResponse]);

    useEffect(() => {
        if (isError) setAlert('Erro ao carregar lista de profissionais', 'danger');
    }, [isError, setAlert]);

    useEffect(() => {
        const handleUpdate = () => refetchProfissionais();
        window.addEventListener('profissionais-atualizados', handleUpdate);
        return () => window.removeEventListener('profissionais-atualizados', handleUpdate);
    }, [refetchProfissionais]);

    const profissionaisList = useMemo(() => {
        const listData = Array.isArray(profData) ? profData : (profData?.data || []);
        
        if (listData.length === 0) return [];

        return listData.map(prof => {
            const userData = prof.user || prof;
            return {
                ...prof,
                id: prof.id || prof._id,
                userId: extractId(userData),
                user: {
                    ...userData,
                    id: extractId(userData),
                    _id: extractId(userData)
                },
                unidadesCount: prof.unidadesCount || 0,
                servicosCount: prof.servicosCount || 0,
                profissionalInfo: {
                    status: prof.status || 'ativo',
                    disponivelParaAgendamento: prof.disponivelParaAgendamento !== false
                }
            };
        });
    }, [profData]);

    const totalProfissionais = Array.isArray(profData) ? profData.length : (profData?.total || profData?.count || 0);

    const handleNovoProfissional = useCallback(() => navigate('/minha-conta/usuarios/novo', { state: { isCreatingMode: true, contaId, defaultRole: 'profissional', allowedRoles: ['profissional'] } }), [navigate, contaId]);
    const handleEditarProfissional = useCallback((userId) => { navigate(`/minha-conta/profissionais/${userId}/editar`); setSelectedProfissional(null); }, [navigate]);
    const handleConfigurarUnidades = useCallback((userId) => { navigate(`/minha-conta/profissionais/${userId}/editar?tab=units`); setSelectedProfissional(null); }, [navigate]);
    const handleConfigurarServicos = useCallback((userId) => { navigate(`/minha-conta/profissionais/${userId}/editar?tab=services`); setSelectedProfissional(null); }, [navigate]);

    const handleReload = useCallback(() => {
        refetchProfissionais();
    }, [refetchProfissionais]);

    const handleFilterChange = (e) => {
        setLocalFilters({ ...localFilters, [e.target.name]: e.target.value });
    };

    const handleApplyFilters = (e) => { 
        if (e) e.preventDefault();
        setActiveFilters(localFilters);
        setCurrentPage(1); 
    };

    const handleClearFilters = () => {
        setLocalFilters(initialFilterState);
        setActiveFilters(initialFilterState);
        setCurrentPage(1);
    };

    const hasFilters = localFilters.search !== '' || localFilters.unidade !== '' || (localFilters.status !== '' && localFilters.status !== 'todos');

    if (loadingProfissionais && profissionaisList.length === 0) {
        return (
            <div className="page-management-container">
                <div className="page-header"><h1 className="page-title"><Briefcase className="icon-title"/>Profissionais</h1></div>
                <div className="flex justify-center items-center py-12">
                    <Loader2 size={32} className="animate-spin text-blue-600" />
                </div>
            </div>
        );
    }

    return (
        <PermissionGuard requiredPermission="equipe:view">
            <div className="page-management-container">
                <PageHeader
                    icon={<Briefcase />}
                    title="Profissionais"
                    actions={<>
                        <div className="view-switcher">
                            <button onClick={() => setViewMode('grid')} className={viewMode === 'grid' ? 'is-active' : ''} title="Cards">
                                <Grid3x3 size={16}/>
                            </button>
                            <button onClick={() => setViewMode('table')} className={viewMode === 'table' ? 'is-active' : ''} title="Tabela">
                                <List size={16}/>
                            </button>
                        </div>
                        <button onClick={() => setShowFilters(!showFilters)} className="action-button action-button-secondary">
                            <Filter size={18} className="icon"/>
                            {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                        </button>
                    </>}
                />

                {/* FILTROS */}
                {showFilters && (
                    <div className="content-panel mb-4 p-0 bg-transparent border-0 shadow-none">
                        <form onSubmit={handleApplyFilters} className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                                
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="filter-search" className="text-xs font-semibold text-gray-600 uppercase">Buscar Nome</label>
                                    <div className="relative">
                                        <Search size={16} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                        <input 
                                            type="text" 
                                            id="filter-search"
                                            name="search"
                                            value={localFilters.search} 
                                            onChange={handleFilterChange} 
                                            placeholder="Nome..." 
                                            className="form-input text-sm py-2 px-3 pl-10 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all w-full"
                                        />
                                    </div>
                                </div>
                                
                                <div className="flex flex-col gap-1">
                                    <label htmlFor="filter-unidade" className="text-xs font-semibold text-gray-600 uppercase">Unidade</label>
                                    <div className="relative">
                                        <select 
                                            id="filter-unidade"
                                            name="unidade"
                                            value={localFilters.unidade} 
                                            onChange={handleFilterChange} 
                                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white w-full appearance-none pr-10"
                                        >
                                            <option value="">Todas as unidades</option>
                                            {unidadesDaConta.map(u => (
                                                <option key={u._id || u.id} value={u._id || u.id}>
                                                    {u.nome}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>

                                <div className="flex flex-col gap-1">
                                    <label htmlFor="filter-status" className="text-xs font-semibold text-gray-600 uppercase">Status</label>
                                    <div className="relative">
                                        <select 
                                            id="filter-status"
                                            name="status"
                                            value={localFilters.status} 
                                            onChange={handleFilterChange} 
                                            className="form-input text-sm py-2 px-3 rounded border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all bg-white w-full appearance-none pr-10"
                                        >
                                            <option value="todos">Todos os status</option>
                                            <option value="ativo">Apenas Ativos</option>
                                            <option value="inativo">Apenas Inativos</option>
                                            <option value="desligado">Desligado</option>
                                        </select>
                                        <ChevronDown size={16} className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" />
                                    </div>
                                </div>

                                {/* Antes reinventava cor do zero em Tailwind (bg-blue-600,
                                    borda vermelha custom) e o texto "Filtrar" só aparecia
                                    acima de 1280px (hidden xl:inline) — mesmo padrão já
                                    corrigido em FiltroAgendamentos.js/FiltroClientes.js */}
                                <div className="flex gap-2 h-full">
                                    <button
                                        type="submit"
                                        className="action-button action-button-primary flex-1 justify-center"
                                        title="Aplicar Filtros"
                                    >
                                        <Search size={18} className="icon" /> Aplicar Filtros
                                    </button>

                                    {hasFilters && (
                                        <button
                                            type="button"
                                            onClick={handleClearFilters}
                                            className="action-button action-button-secondary justify-center"
                                            title="Limpar Filtros"
                                        >
                                            <X size={18} className="icon" />
                                        </button>
                                    )}
                                </div>
                            </div>
                        </form>
                    </div>
                )}

                {viewMode === 'grid' ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
                        {profissionaisList.map(profissional => (
                            <ProfissionalCard
                                key={profissional.id || profissional._id}
                                profissional={profissional}
                                onOpenActions={setSelectedProfissional}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="table-container">
                        <div className="overflow-x-auto">
                            <table className="styled-table w-full">
                                <thead>
                                    <tr>
                                        <th style={{ width: '35%' }}>Profissional</th>
                                        <th style={{ width: '15%' }}>Telefone</th>
                                        <th className="text-center" style={{ width: '10%' }}>Serviços</th>
                                        <th className="text-center" style={{ width: '10%' }}>Unidades</th>
                                        <th className="text-center" style={{ width: '10%' }}>Status</th>
                                        <th className="text-right pr-4" style={{ width: '20%' }}>Ações</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {profissionaisList.map(profissional => (
                                        <ProfissionaisRow
                                            key={profissional.id || profissional._id}
                                            profissional={profissional}
                                            onOpenActions={setSelectedProfissional}
                                        />
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}

                {totalProfissionais === 0 && (
                    <div className="table-empty-state py-12 mt-6 border rounded-lg">
                        <div className="bg-gray-100 p-4 rounded-full mb-4 inline-flex">
                            <Briefcase className="text-gray-400" size={40} />
                        </div>
                        <h3 className="text-lg font-medium text-gray-900 mb-1">
                            Nenhum profissional encontrado
                        </h3>
                        <p className="text-gray-500 max-w-sm mx-auto mb-4">
                            {hasFilters
                                ? 'Tente ajustar os filtros de busca'
                                : 'Comece adicionando um novo profissional'}
                        </p>
                        {!hasFilters && (
                            <button
                                onClick={handleNovoProfissional}
                                className="action-button action-button-primary mx-auto"
                            >
                                <UserPlus size={18} className="icon" />
                                Adicionar Profissional
                            </button>
                        )}
                    </div>
                )}

                {totalProfissionais > profissionaisPerPage && (
                    <div className="mt-6">
                        <Pagination 
                            itemsPerPage={profissionaisPerPage} 
                            totalItems={totalProfissionais} 
                            onPageChange={setCurrentPage} 
                            currentPage={currentPage} 
                        />
                    </div>
                )}
            </div>

            <FabButton onClick={handleNovoProfissional} title="Adicionar Profissional" icon={<UserPlus size={26} className="icon"/>}/>

            <ActionsDrawer
                open={!!selectedProfissional}
                onClose={() => setSelectedProfissional(null)}
                title={selectedProfissional?.user?.name || 'Profissional'}
                actions={selectedProfissional ? [
                    {
                        key: 'configurar-unidades',
                        label: 'Configurar Unidades',
                        icon: <MapPin size={20} className="icon" />,
                        onClick: () => handleConfigurarUnidades(selectedProfissional.user?.id || selectedProfissional.user?._id),
                    },
                    {
                        key: 'configurar-servicos',
                        label: 'Configurar Serviços',
                        icon: <Settings size={20} className="icon" />,
                        onClick: () => handleConfigurarServicos(selectedProfissional.user?.id || selectedProfissional.user?._id),
                    },
                    {
                        key: 'editar',
                        label: 'Visualizar / Editar Perfil',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEditarProfissional(selectedProfissional.user?.id || selectedProfissional.user?._id),
                    },
                ] : []}
            />
        </PermissionGuard>
    );
};

export default ProfissionaisPage;